import { ExperienceDetailConfig, ExperienceType } from './types';

export const getExperienceConfig = (
  type: ExperienceType
): ExperienceDetailConfig => {
  const configs: Record<ExperienceType, ExperienceDetailConfig> = {
    activity: {
      pageTitle: 'Activity Details',
      metaDescription: 'Discover amazing activities and experiences',
      apiEndpoints: {
        details: (slug: string) =>
          `${process.env.NEXT_PUBLIC_API_URL || ''}/travel/activities/v1/details/${slug}`,
        related: '/api/travel/activities/list-all',
      },
      sectionOrder: [
        'header',
        'description',
        'videos',
        'greatFor',
        'pickupPoint',
        'map',
        'highlights',
        'itinerary',
        'inclusions',
        'exclusions',
        'whatToExpect',
        'keyDetails',
        'faq',
        'notices',
        'guide',
        'related',
      ],
      sections: {
        header: { component: 'Header', order: 1 },
        description: { component: 'Description', order: 2 },
        videos: {
          component: 'Videos',
          order: 3,
          condition: (exp: unknown) => {
            const e = exp as {
              tourGuideVideos?: unknown[];
              media?: Array<{ format?: string }>;
            };
            return (
              (Array.isArray(e?.tourGuideVideos) &&
                e.tourGuideVideos.length > 0) ||
              (Array.isArray(e?.media) &&
                e.media.some(item => item.format === 'video'))
            );
          },
        },
        greatFor: {
          component: 'GreatFor',
          order: 4,
          condition: (exp: unknown) => {
            const e = exp as { travellerTypes?: unknown[] };
            return (
              Array.isArray(e?.travellerTypes) && e.travellerTypes.length > 0
            );
          },
        },
        pickupPoint: {
          component: 'PickupPoint',
          order: 5,
          condition: (exp: unknown) => {
            const e = exp as { activities?: unknown[] };
            return Array.isArray(e?.activities) && e.activities.length > 0;
          },
        },
        map: {
          component: 'Map',
          order: 6,
          condition: (exp: unknown) => {
            const e = exp as { activities?: unknown[] };
            return Array.isArray(e?.activities) && e.activities.length > 0;
          },
        },
        highlights: { component: 'Highlights', order: 7 },
        itinerary: {
          component: 'Itinerary',
          order: 8,
          condition: (exp: unknown) => {
            const e = exp as { itinerary?: unknown[] };
            return Array.isArray(e?.itinerary) && e.itinerary.length > 0;
          },
        },
        inclusions: {
          component: 'Inclusions',
          order: 9,
          condition: (exp: unknown) => {
            const e = exp as {
              inclusions?: unknown[];
              included?: unknown[];
              activityIncludes?: unknown[];
            };
            return (
              (Array.isArray(e?.inclusions) && e.inclusions.length > 0) ||
              (Array.isArray(e?.included) && e.included.length > 0) ||
              (Array.isArray(e?.activityIncludes) &&
                e.activityIncludes.length > 0)
            );
          },
        },
        exclusions: { component: 'Exclusions', order: 10 },
        whatToExpect: { component: 'WhatToExpect', order: 11 },
        keyDetails: {
          component: 'KeyDetails',
          order: 12,
          condition: (exp: unknown) => {
            const e = exp as { needToKnow?: unknown[] };
            return Array.isArray(e?.needToKnow) && e.needToKnow.length > 0;
          },
        },
        faq: {
          component: 'FAQ',
          order: 13,
          condition: (exp: unknown) => {
            const e = exp as { faq?: unknown[] };
            return Array.isArray(e?.faq) && e.faq.length > 0;
          },
        },
        notices: {
          component: 'Notices',
          order: 14,
          condition: (exp: unknown) => {
            const e = exp as { notices?: unknown[] };
            return Array.isArray(e?.notices) && e.notices.length > 0;
          },
        },
        guide: {
          component: 'Guide',
          order: 15,
          condition: (exp: unknown) => {
            const e = exp as { guide?: unknown };
            return !!e?.guide;
          },
        },
        related: { component: 'Related', order: 16 },
      },
    },
    package: {
      pageTitle: 'Package Details',
      metaDescription: 'Explore curated travel packages',
      apiEndpoints: {
        details: (slug: string) =>
          `${process.env.NEXT_PUBLIC_API_URL || ''}/travel/packages/v1/details/${slug}`,
        related: '/api/travel/packages/list-all',
      },
      sectionOrder: [
        'header',
        'description',
        'videoGallery',
        'targetAudience',
        'itinerary',
        'transfers',
        'vehicles',
        'accommodationOptions',
        'airports',
        'highlights',
        'inclusions',
        'exclusions',
        'whatToExpect',
        'keyDetails',
        'faq',
        'notices',
        'guide',
        'related',
      ],
      sections: {
        header: { component: 'Header', order: 1 },
        description: { component: 'Description', order: 2 },
        videoGallery: {
          component: 'VideoGallery',
          order: 3,
          condition: (exp: unknown) => {
            const e = exp as {
              videos?: unknown[];
              reviewVideos?: unknown[];
              media?: Array<{ format?: string }>;
            };
            return (
              (Array.isArray(e?.videos) && e.videos.length > 0) ||
              (Array.isArray(e?.reviewVideos) && e.reviewVideos.length > 0) ||
              (Array.isArray(e?.media) &&
                e.media.some(item => item.format === 'video'))
            );
          },
        },
        targetAudience: {
          component: 'TargetAudience',
          order: 4,
          condition: (exp: unknown) => {
            const e = exp as {
              travellerTypes?: unknown[];
              targetAudience?: unknown[];
            };
            return (
              (Array.isArray(e?.travellerTypes) &&
                e.travellerTypes.length > 0) ||
              (Array.isArray(e?.targetAudience) && e.targetAudience.length > 0)
            );
          },
        },
        itinerary: {
          component: 'Itinerary',
          order: 5,
          // Remove condition - let the component handle the null check
        },
        transfers: {
          component: 'Transfers',
          order: 6,
          condition: (exp: unknown) => {
            const e = exp as { transfers?: unknown[] };
            return Array.isArray(e?.transfers) && e.transfers.length > 0;
          },
        },
        vehicles: {
          component: 'Vehicles',
          order: 7,
          condition: (exp: unknown) => {
            const e = exp as { vehicles?: unknown[] };
            return Array.isArray(e?.vehicles) && e.vehicles.length > 0;
          },
        },
        accommodationOptions: {
          component: 'AccommodationOptions',
          order: 8,
          condition: (exp: unknown) => {
            const e = exp as {
              roomOptions?: unknown[];
              hotels?: unknown[];
              accommodations?: unknown[];
            };
            return (
              (Array.isArray(e?.roomOptions) && e.roomOptions.length > 0) ||
              (Array.isArray(e?.hotels) && e.hotels.length > 0) ||
              (Array.isArray(e?.accommodations) && e.accommodations.length > 0)
            );
          },
        },
        airports: {
          component: 'Airports',
          order: 9,
          condition: (exp: unknown) => {
            const e = exp as { airports?: unknown[] };
            return Array.isArray(e?.airports) && e.airports.length > 0;
          },
        },
        highlights: { component: 'Highlights', order: 10 },
        inclusions: {
          component: 'Inclusions',
          order: 11,
          condition: (exp: unknown) => {
            const e = exp as { inclusions?: unknown[]; included?: unknown[] };
            return (
              (Array.isArray(e?.inclusions) && e.inclusions.length > 0) ||
              (Array.isArray(e?.included) && e.included.length > 0)
            );
          },
        },
        exclusions: { component: 'Exclusions', order: 12 },
        whatToExpect: { component: 'WhatToExpect', order: 13 },
        keyDetails: {
          component: 'KeyDetails',
          order: 14,
          condition: (exp: unknown) => {
            const e = exp as { needToKnow?: unknown[] };
            return Array.isArray(e?.needToKnow) && e.needToKnow.length > 0;
          },
        },
        faq: {
          component: 'FAQ',
          order: 15,
          condition: (exp: unknown) => {
            const e = exp as { faq?: unknown[] };
            return Array.isArray(e?.faq) && e.faq.length > 0;
          },
        },
        notices: {
          component: 'Notices',
          order: 16,
          condition: (exp: unknown) => {
            const e = exp as { notices?: unknown[] };
            return Array.isArray(e?.notices) && e.notices.length > 0;
          },
        },
        guide: {
          component: 'Guide',
          order: 17,
          condition: (exp: unknown) => {
            const e = exp as { guide?: unknown };
            return !!e?.guide;
          },
        },
        related: { component: 'Related', order: 18 },
      },
    },
    stay: {
      pageTitle: 'Nexgo Space Details',
      metaDescription: 'Find the perfect accommodation',
      apiEndpoints: {
        details: (id: string) =>
          `${process.env.NEXT_PUBLIC_API_URL || ''}/stay-experiences/v1/${id}`,
        related: (id: string) =>
          `${process.env.NEXT_PUBLIC_API_URL || ''}/stay-experiences/v1/${id}/by-same-guide`,
      },
      sectionOrder: [
        'header',
        'description',
        'videos',
        'greatFor',
        'amenities',
        'accommodationOptions',
        'activities',
        'keyDetails',
        'inclusions',
        'notices',
        'faq',
        'guide',
        'related',
      ],
      sections: {
        header: { component: 'Header', order: 1 },
        description: { component: 'Description', order: 2 },
        videos: {
          component: 'Videos',
          order: 3,
          condition: (exp: unknown) => {
            const e = exp as {
              tourGuideVideos?: unknown[];
              media?: Array<{ format?: string }>;
            };
            return (
              (Array.isArray(e?.tourGuideVideos) &&
                e.tourGuideVideos.length > 0) ||
              (Array.isArray(e?.media) &&
                e.media.some(item => item?.format === 'video'))
            );
          },
        },
        greatFor: {
          component: 'GreatFor',
          order: 4,
          condition: (exp: unknown) => {
            const e = exp as { travellerTypes?: unknown[] };
            return (
              Array.isArray(e?.travellerTypes) && e.travellerTypes.length > 0
            );
          },
        },
        amenities: {
          component: 'Amenities',
          order: 5,
          condition: (exp: unknown) => {
            const e = exp as {
              amenities?: unknown[];
              facilities?: unknown[];
            };
            return (
              (Array.isArray(e?.amenities) && e.amenities.length > 0) ||
              (Array.isArray(e?.facilities) && e.facilities.length > 0)
            );
          },
        },
        accommodationOptions: {
          component: 'AccommodationOptions',
          order: 6,
          condition: (exp: unknown) => {
            const e = exp as { roomOptions?: unknown[] };
            return Array.isArray(e?.roomOptions) && e.roomOptions.length > 0;
          },
        },
        activities: {
          component: 'Activities',
          order: 7,
          condition: (exp: unknown) => {
            const e = exp as { activities?: unknown[] };
            return Array.isArray(e?.activities) && e.activities.length > 0;
          },
        },
        keyDetails: {
          component: 'KeyDetails',
          order: 8,
          condition: (exp: unknown) => {
            const e = exp as {
              needToKnow?: unknown[];
              guidelines?: { needToKnow?: unknown[] };
            };
            return (
              (Array.isArray(e?.needToKnow) && e.needToKnow.length > 0) ||
              (Array.isArray(e?.guidelines?.needToKnow) &&
                e.guidelines.needToKnow.length > 0)
            );
          },
        },
        inclusions: {
          component: 'Inclusions',
          order: 9,
          condition: (exp: unknown) => {
            const e = exp as {
              inclusions?: unknown[];
              included?: unknown[];
              activityIncludes?: unknown[];
            };
            return (
              (Array.isArray(e?.inclusions) && e.inclusions.length > 0) ||
              (Array.isArray(e?.included) && e.included.length > 0) ||
              (Array.isArray(e?.activityIncludes) &&
                e.activityIncludes.length > 0)
            );
          },
        },
        notices: {
          component: 'Notices',
          order: 10,
          condition: (exp: unknown) => {
            const e = exp as {
              notices?: unknown[];
              guidelines?: { notices?: unknown[] };
            };
            return (
              (Array.isArray(e?.notices) && e.notices.length > 0) ||
              (Array.isArray(e?.guidelines?.notices) &&
                e.guidelines.notices.length > 0)
            );
          },
        },
        faq: {
          component: 'FAQ',
          order: 11,
          condition: (exp: unknown) => {
            const e = exp as {
              faq?: unknown[];
              guidelines?: { faqs?: unknown[] };
            };
            return (
              (Array.isArray(e?.faq) && e.faq.length > 0) ||
              (Array.isArray(e?.guidelines?.faqs) &&
                e.guidelines.faqs.length > 0)
            );
          },
        },
        guide: {
          component: 'Guide',
          order: 12,
          condition: (exp: unknown) => {
            const e = exp as { guide?: unknown; guideDetails?: unknown };
            return !!e?.guide || !!e?.guideDetails;
          },
        },
        related: { component: 'Related', order: 13 },
      },
    },
    'riders-corner': {
      pageTitle: 'Riders Corner Details',
      metaDescription: 'Discover amazing motorcycle routes and trails',
      apiEndpoints: {
        details: (slug: string) =>
          `${process.env.NEXT_PUBLIC_API_URL || ''}/travel/riders-corner/v1/details/${slug}`,
        related: '/api/travel/riders-corner/list-all',
      },
      sectionOrder: ['header', 'description', 'related'],
      sections: {
        header: { component: 'Header', order: 1 },
        description: { component: 'Description', order: 2 },
        related: { component: 'Related', order: 3 },
      },
    },
  };

  return configs[type];
};
